import { LineOrDirectoryPatch, Station, StationRole } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { enqueueError, enqueueSuccess } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';

import { linesSlice } from '../store/linesSlice';
import { stationsSlice } from '../store/stationsSlice';
import { trailersSlice } from '../store/trailersSlice';
import { truckDriversSlice } from '../store/truckDriversSlice';
import { AsyncThunkActionVoid } from './thunkActions';

export function createDirectory(name: string, parentGuid?: string): AsyncThunkActionVoid {
  return async function createDirectoryThunkAction(dispatch) {
    const createdDirectory = await designService.directories.createDirectory({ name, parentGuid });
    dispatch(linesSlice.actions.singleDirectoryEdited(createdDirectory));
  };
}

export function patchDirectory(directoryGuid: string, patch: LineOrDirectoryPatch): AsyncThunkActionVoid {
  return async function setDirectoryNameThunkAction(dispatch) {
    const editedDirectory = await designService.directories.patchDirectory(directoryGuid, patch);
    dispatch(linesSlice.actions.singleDirectoryEdited(editedDirectory));
  };
}

export function deleteDirectory(directoryGuid: string): AsyncThunkActionVoid {
  return async function deleteDirectoryThunkAction(dispatch, getState) {
    const directory = getState().lines.directories[directoryGuid];
    if (directory == null) throw new Error('Unable to find directory to delete.');

    if (!confirm(`Are you sure you want to delete directory ${directory.name}?`)) return;

    const spinnerId = dispatch(addSpinnerMessage('Deleting directory...'));

    try {
      await designService.directories.deleteDirectory(directory.guid);
    } catch (error) {
      console.error(error);
      dispatch(enqueueError('Unable to delete directory.'));
      return;
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }

    dispatch(linesSlice.actions.singleDirectoryDeleted({ guid: directory.guid, parentGuid: directory.parentGuid }));
    dispatch(enqueueSuccess('Directory deleted.'));
  };
}

export function createLine(name: string, parentGuid?: string): AsyncThunkActionVoid {
  return async function createLineThunkAction(dispatch) {
    const createdLine = await designService.lines.createLine({ name, parentGuid });
    dispatch(linesSlice.actions.singleLineEdited(createdLine));
  };
}

export function patchLine(lineGuid: string, patch: LineOrDirectoryPatch): AsyncThunkActionVoid {
  return async function setLineNameThunkAction(dispatch) {
    const response = await designService.lines.patchLine(lineGuid, patch);
    dispatch(linesSlice.actions.singleLineEdited(response));
  };
}

export function deleteLine(lineGuid: string): AsyncThunkActionVoid {
  return async function deleteLineThunkAction(dispatch, getState) {
    const line = getState().lines.lines[lineGuid];
    if (line == null) throw new Error('Unable to find line to delete.');

    if (!confirm(`Are you sure you want to delete line ${line.name}?`)) return;

    const spinnerId = dispatch(addSpinnerMessage('Deleting line...'));

    try {
      await designService.lines.deleteLine(lineGuid);
    } catch (error) {
      console.error(error);
      dispatch(enqueueError('Unable to delete line.'));
      return;
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }

    dispatch(linesSlice.actions.singleLineDeleted({ guid: lineGuid, parentGuid: line.parentGuid }));
    dispatch(enqueueSuccess('Line deleted.'));
  };
}

export function fullyLoadStationIfNeeded(stationGuid: string): AsyncThunkActionVoid {
  return async function fullyLoadStationIfNeededThunkAction(dispatch, getState) {
    if (getState().stations.fullyLoadedStations[stationGuid] !== true) {
      await dispatch(fullyLoadStation(stationGuid));
    }
  };
}

export function fullyLoadStation(stationGuid: string): AsyncThunkActionVoid {
  return async function fullyLoadStationThunkAction(dispatch) {
    const spinnerId = dispatch(addSpinnerMessage('Loading component designs...'));

    try {
      const station = await designService.stations.getStation(stationGuid);
      dispatch(stationsSlice.actions.loadedFromServer({ stations: [station], shallow: false }));
    } catch (error) {
      console.error(error);
      dispatch(enqueueError('Unable to load component designs.'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function createStation(
  name: string,
  stationRole: StationRole,
  followsSchedule: boolean,
  parentGuid?: string,
): AsyncThunkActionVoid {
  return async function createStationThunkAction(dispatch) {
    const creationResponse = await designService.stations.addStation(name, stationRole, followsSchedule);
    const station = creationResponse.result;

    dispatch(stationsSlice.actions.singleStationCreated({ station, parentGuid }));

    if (parentGuid) {
      await designService.lines.addStationToLine(parentGuid, station.guid);
    }
  };
}

export function setStationName(stationGuid: string, name: string): AsyncThunkActionVoid {
  return async function setStationNameThunkAction(dispatch) {
    const response = await designService.stations.patchStation(stationGuid, { name });
    dispatch(stationsSlice.actions.singleStationEdited(response.result));
  };
}

export function insertStation(stationGuid: string, lineGuid: string): AsyncThunkActionVoid {
  return async function addStationToLineThunkAction(dispatch) {
    const spinnerId = dispatch(addSpinnerMessage('Adding station to line...'));

    try {
      await designService.lines.addStationToLine(lineGuid, stationGuid);
    } catch (error) {
      console.log(error);
      dispatch(enqueueError('Unable to add station to line.'));
      return;
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }

    dispatch(linesSlice.actions.stationInserted({ lineGuid, insertion: { guid: stationGuid, xIndex: 0, yIndex: 0 } }));
  };
}

export function unInsertStation(stationGuid: string, lineGuid: string): AsyncThunkActionVoid {
  return async function removeStationFromLineThunkAction(dispatch) {
    const spinnerId = dispatch(addSpinnerMessage('Removing station from line...'));

    try {
      await designService.lines.removeStationFromLine(lineGuid, stationGuid);
    } catch (error) {
      console.log(error);
      dispatch(enqueueError('Unable to remove station from line.'));
      return;
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }

    dispatch(linesSlice.actions.stationUnInserted({ lineGuid, stationGuid }));
    dispatch(enqueueSuccess('Station removed.'));
  };
}

export function clearStation(stationGuid: string): AsyncThunkActionVoid {
  return async function clearStationThunkAction(dispatch) {
    const spinnerId = dispatch(addSpinnerMessage('Clearing station...'));

    let syncResult;

    try {
      syncResult = await designService.stations.clearStation(stationGuid);
    } catch (error) {
      console.log(error);
      dispatch(enqueueError('Unable to clear station.'));
      return;
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }

    dispatch(stationsSlice.actions.loadedFromServer({ stations: [syncResult.result], shallow: false }));

    dispatch(enqueueSuccess('Station cleared.'));
  };
}

export function deleteStation(station: Station): AsyncThunkActionVoid {
  return async function deleteStationThunkAction(dispatch) {
    if (!confirm(`Are you sure you want to delete station ${station.name}?`)) return;

    const spinnerId = dispatch(addSpinnerMessage('Deleting station...'));

    try {
      await designService.stations.deleteStation(station.guid);
    } catch (error) {
      console.log(error);
      dispatch(enqueueError('Unable to delete station.'));
      return;
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }

    dispatch(stationsSlice.actions.singleStationDeleted({ guid: station.guid }));
    dispatch(enqueueSuccess('Station deleted.'));
  };
}

export function createTrailer(name: string): AsyncThunkActionVoid {
  return async function createTrailerThunkAction(dispatch) {
    const spinnerId = dispatch(addSpinnerMessage('Creating trailer...'));

    try {
      const trailer = await designService.trailers.insert(name);
      dispatch(trailersSlice.actions.trailerCreated(trailer));
    } catch (error) {
      console.log(error);
      dispatch(enqueueError('Unable to create trailer.'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function setArchiveStatusForTrailer(guid: string, archived: boolean): AsyncThunkActionVoid {
  return async function setArchiveStatusForTrailerThunkAction(dispatch) {
    const spinnerMessage = `${archived ? 'Archiving' : 'Unarchiving'} trailer...`;
    const spinnerId = dispatch(addSpinnerMessage(spinnerMessage));

    try {
      await designService.trailers.setArchiveStatus(guid, archived);
      dispatch(trailersSlice.actions.trailerArchivedStatusSet({ guid, archived }));
    } catch (error) {
      console.log(error);
      dispatch(enqueueError(`Unable to ${archived ? '' : 'un'}archive trailer.`));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function createTruckDriver(name: string): AsyncThunkActionVoid {
  return async function createTruckDriverThunkAction(dispatch) {
    const spinnerId = dispatch(addSpinnerMessage('Creating truck driver...'));

    try {
      const truckDriver = await designService.truckDrivers.insert(name);
      dispatch(truckDriversSlice.actions.truckDriverCreated(truckDriver));
    } catch (error) {
      console.log(error);
      dispatch(enqueueError('Unable to create truck driver.'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}

export function setTruckDriverArchiveStatus(guid: string, archived: boolean): AsyncThunkActionVoid {
  return async function setTruckDriverArchiveStatusThunkAction(dispatch) {
    const spinnerMessage = `${archived ? 'Archiving' : 'Unarchiving'} truck driver...`;
    const spinnerId = dispatch(addSpinnerMessage(spinnerMessage));

    try {
      await designService.truckDrivers.setArchiveStatus(guid, archived);
      dispatch(truckDriversSlice.actions.truckDriverArchivedStatusSet({ guid, archived }));
    } catch (error) {
      console.log(error);
      dispatch(enqueueError(`Unable to ${archived ? '' : 'un'}archive truck driver.`));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}
